import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import NewsAPI from '../api/NewsAPI';
import { NewsWithViewStatus, NewsResponse } from '../types/news';

export const useNews = () => {
  const { i18n } = useTranslation();
  const [news, setNews] = useState<NewsWithViewStatus[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchNews = useCallback(async (offset: number = 0, limit: number = 5) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const locale = i18n.language || 'en';
      const response: NewsResponse = await NewsAPI.getNews(locale, limit, offset);
      
      if (offset === 0) {
        setNews(response.news);
        setCurrentOffset(limit);
      } else {
        setNews(prevNews => [...prevNews, ...response.news]);
        setCurrentOffset(prev => prev + limit);
      }
      
      setTotalCount(response.totalCount);
      setHasMore(offset + limit < response.totalCount);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Could not fetch news');
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language]);

  const loadMoreNews = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchNews(currentOffset, 5);
    }
  }, [fetchNews, currentOffset, isLoading, hasMore]);

  const markAsViewedById = useCallback(async (newsId: number) => {
    try {
      await NewsAPI.markAsViewedById(newsId);
      
      setNews(prevNews => 
        prevNews.map(item => 
          item.id === newsId ? { ...item, viewed: true } : item
        )
      );
    } catch (err) {
      console.error('Error marking news as viewed:', err);
    }
  }, []);

  const refreshNews = useCallback(() => {
    setCurrentOffset(0);
    setHasMore(true);
    fetchNews(0, 5);
  }, [fetchNews]);

  const recentNews = news;
  const unreadCount = recentNews.filter(item => !item.viewed).length;

  useEffect(() => {
    fetchNews(0, 5);
  }, [fetchNews]);

  return {
    news: recentNews,
    allNews: news,
    totalCount,
    isLoading,
    error,
    hasMore,
    unreadCount,
    fetchNews,
    loadMoreNews,
    markAsViewedById,
    refreshNews
  };
};
